import type { ReactNode } from 'react';
import { WertEinheit } from './WertEinheit';
import './muster.css';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * KENNWERTTAFEL (Auftrag 17.09.2026, Abschnitt 17)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   „Für Räume, Anlagen, Maschinen, Komponenten usw. ein einheitliches
 *    Detailseitenmuster schaffen."
 *
 * Die Tafel ist der Rumpf jeder Detailseite: Kennwert links, Wert rechts,
 * Einheit getrennt vom Wert (Abschnitt 15). Sie ist eine
 * Beschreibungsliste (`<dl>`), keine Tabelle - es sind Paare, keine Matrix.
 * Vorlesehilfen lesen das Paar dann zusammen.
 *
 * HERKUNFT EINES WERTES
 * ---------------------
 * Diese Anwendung unterscheidet seit langem, woher ein Wert stammt:
 * Modellfeld (grün, hat einen Zielparameter im Modell), Datenfeld (gelb,
 * nur in der Datenbank), Baugruppe (blau). Die Merkmale dafür stehen als
 * `--modellfeld`, `--datenfeld`, `--baugruppe` in `stil/merkmale.css` und
 * werden hier über `herkunft` angeboten - damit der Umzug (U6) diese
 * fachliche Kennzeichnung nicht verliert.
 *
 * Ein fehlender Wert erscheint als „—", nie als leere Zelle: Leer sieht aus
 * wie ein Darstellungsfehler, „—" ist eine Aussage.
 */

export type Herkunft = 'modell' | 'daten' | 'baugruppe';

export interface Kennwert {
  beschriftung: string;
  /** Zahl, Text oder ein fertiger Knoten (z. B. eine `StatusBadge`). */
  wert: ReactNode | number | null | undefined;
  einheit?: string;
  nachkomma?: number;
  /** Kurze Erklärung, woher der Wert kommt oder wie er entstand. */
  hinweis?: string;
  herkunft?: Herkunft;
  /** Über die ganze Breite - für lange Texte und Bemerkungen. */
  breit?: boolean;
}

export interface PropertyGridProps {
  eintraege: Kennwert[];
  /** Spalten der Tafel. Vorgabe 2. */
  spalten?: 1 | 2 | 3;
  /** Engere Zeilen. */
  dicht?: boolean;
  className?: string;
}

function istKnoten(wert: unknown): boolean {
  return typeof wert === 'object' && wert !== null;
}

export function PropertyGrid({
  eintraege, spalten = 2, dicht, className,
}: PropertyGridProps) {
  return (
    <dl
      className={[
        'm-kennwerte', `m-kennwerte-${spalten}`,
        dicht ? 'm-kennwerte-dicht' : '', className ?? '',
      ].filter(Boolean).join(' ')}
    >
      {eintraege.map((e) => (
        <div
          className={[
            'm-kennwert',
            e.breit ? 'm-kennwert-breit' : '',
            e.herkunft ? `m-herkunft-${e.herkunft}` : '',
          ].filter(Boolean).join(' ')}
          key={e.beschriftung}
        >
          <dt className="m-kennwert-name">
            {e.beschriftung}
            {e.hinweis && (
              <span className="m-kennwert-hinweis" title={e.hinweis} aria-hidden="true">
                ?
              </span>
            )}
          </dt>
          <dd className="m-kennwert-wert">
            {istKnoten(e.wert)
              ? (e.wert as ReactNode)
              : (
                <WertEinheit
                  wert={e.wert as number | string | null | undefined}
                  einheit={e.einheit}
                  nachkomma={e.nachkomma}
                />
              )}
            {e.hinweis && <span className="m-nur-vorlesen"> ({e.hinweis})</span>}
          </dd>
        </div>
      ))}
    </dl>
  );
}
